import { Navigate, Outlet, ScrollRestoration, createBrowserRouter, useParams, useSearchParams } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { Suspense, lazy } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { basename, is_dummy_api_mode, is_history_enabled } from './utils/config'

import AuthRedirect from './components/AuthRedirect'
import GlobalLayout from './components/GlobalLayout/GlobalLayout'

import {aboutLoader, faqLoader, privacypolicyLoader, childsafetyLoader, tradelawLoader, contactLoader} from './pages/About/loader'
import AcceptRules from './components/AcceptRules/AcceptRules'
import { AlbumCacheProvider, PhotosCacheProvider, SearchPhotosCacheProvider } from './hooks/photos-cache'
import { AlbumAPI, DMAPI, FavoriteAPI, OpenAPIResponseData, PhotoAPI, SearchAPI, ShareAPI, UserAPI } from './api/api-switcher'
import AboutLocalLayout from './pages/About/LocalLayout'
import { emailLoader } from './pages/Setting/loader'
import HeaderWith404 from './components/HeaderWith404'
import { usePageTracking } from './hooks/usePageTracking'
import { useGetCacheSharePassword } from './hooks/cache-share-password'
import UrgeReload from './components/UrgeReload'
import { invalidateFavoriteListOnSuccess } from './api/favorite-query'
import { invalidateTrashOnSuccess } from './api/trash-query'

const urgeReload = () => ({default: UrgeReload})

import EditProfile from './pages/User/EditProfile'
import useCapacitorBack from './hooks/useCapacitorBack'
import useDeeplinkListener from './hooks/useDeeplinkListener'
import { desktopHandoffRoutes } from './pages/Auth/desktopHandoffRoutes'
const Home = lazy(() => import('./pages/Home/Home').catch(urgeReload))
const MyPhoto = lazy(() => import('./pages/MyPhoto/MyPhoto').catch(urgeReload))
const MyPhotoPhoto = lazy(() => import('./pages/MyPhoto/Photo').catch(urgeReload))
const Notification = lazy(() => import('./pages/Notification/Notification').catch(urgeReload))
const Login = lazy(() => import('./pages/Login/Login').catch(urgeReload))
const Search = lazy(() => import('./pages/Search/Search').catch(urgeReload))
const Labo = lazy(() => import('./pages/Labo').catch(urgeReload))
const FavoriteList = lazy(() => import('./pages/Favorites/FavoriteList').catch(urgeReload))
const Favorite = lazy(() => import('./pages/Favorites/Favorite').catch(urgeReload))
const FavoritePhoto = lazy(() => import('./pages/Favorites/Photo').catch(urgeReload))
const CreateFavorite = lazy(() => import('./pages/Favorites/CreateFavorite').catch(urgeReload))
const EditFavorite = lazy(() => import('./pages/Favorites/EditFavorite').catch(urgeReload))
const HeaderWithError = lazy(() => import('./pages/HeaderWithError').catch(urgeReload))
const CreateAlbum = lazy(() => import('./pages/MyAlbum/CreateAlbum').catch(urgeReload))
const AddressBook = lazy(() => import('./pages/AddressBook/AddressBook').catch(urgeReload))
const UserRouter = lazy(() => import('./pages/User/UserRouter').catch(urgeReload))
const BookUser = lazy(() => import('./pages/AddressBook/BookUser').catch(urgeReload))
const GroupEditor = lazy(() => import('./pages/Group/GroupEditor').catch(urgeReload))
const Group = lazy(() => import('./pages/Group/Group').catch(urgeReload))
const GroupUserSelector = lazy(() => import('./pages/GroupUserSelector/GroupUserSelector').catch(urgeReload))
// 本来の DM 一覧は DMList。アドレス帳機能が未実装で現状は全ユーザー検索しか無いため、
// 暫定で下の /dm ルートに DMListSearchFromAllUsers をリンクしている。アドレス帳実装後に
// DMList を復活させる予定なので削除しない（DMList は useSuspenseQuery(dmKeys.list) 移行済み、
// この行と /dm ルートの element を戻すだけで動く）。
//const DMList = lazy(() => import('./pages/DM/DMList').catch(urgeReload))
const CreateDM = lazy(() => import('./pages/DM/CreateDM/CreateDM').catch(urgeReload))
const DMListSearchFromAllUsers = lazy(() => import('./pages/DM/DMListSearchFromAllUsers').catch(urgeReload))
const DM = lazy(() => import('./pages/DM/DM').catch(urgeReload))
const DMPhoto = lazy(() => import('./pages/DM/Photo').catch(urgeReload))
const MyAlbum = lazy(() => import('./pages/MyAlbum/MyAlbum').catch(urgeReload))
const Album = lazy(() => import('./pages/MyAlbum/Album').catch(urgeReload))
const EditAlbum = lazy(() => import('./pages/MyAlbum/EditAlbum').catch(urgeReload))
const ChangeShare = lazy(() => import('./pages/MyAlbum/ChangeShare').catch(urgeReload))
const AlbumPhoto = lazy(() => import('./pages/MyAlbum/Photo').catch(urgeReload))
const ShareAlbumList = lazy(() => import('./pages/ShareAlbum/ShareAlbumList').catch(urgeReload))
const CreateShareAlbum = lazy(() => import('./pages/ShareAlbum/CreateShareAlbum').catch(urgeReload))
const ShareAlbum = lazy(() => import('./pages/ShareAlbum/ShareAlbum').catch(urgeReload))
const ApprovalAwaitingList = lazy(() => import('./pages/ShareAlbum/ApprovalAwaitingList').catch(urgeReload))
const InvitationList = lazy(() => import('./pages/ShareAlbum/InvitationList').catch(urgeReload))
const EditShareAlbum = lazy(() => import('./pages/ShareAlbum/EditShareAlbum').catch(urgeReload))
const ShareAlbumMember = lazy(() => import('./pages/ShareAlbum/MemberList').catch(urgeReload))
const SharePhoto = lazy(() => import('./pages/ShareAlbum/Photo').catch(urgeReload))
const SelectOwner = lazy(() => import('./pages/ShareAlbum/SelectOwner').catch(urgeReload))
const AddUsers = lazy(() => import('./pages/ShareAlbum/AddUsers').catch(urgeReload))
const Setting = lazy(() => import('./pages/Setting/Setting').catch(urgeReload))
const DisplayAndAccessibilitySetting = lazy(() => import('./pages/Setting/DisplayAndAccessibilitySetting').catch(urgeReload))
const AccountSetting = lazy(() => import('./pages/Setting/AccountSetting').catch(urgeReload))
const ChangeAccountPassword = lazy(() => import('./pages/Setting/ChangeAccountPassword').catch(urgeReload))
// FEATURE_PASSKEY が false のビルドではルートごと消えるので、未使用の lazy としてチャンクごと外れるよう pure 指定する
const PasskeySetting = /* @__PURE__ */ lazy(() => import('./pages/Setting/PasskeySetting').catch(urgeReload))
const ChangeAccountEmail = lazy(() => import('./pages/Setting/ChangeAccountEmail').catch(urgeReload))
const ConfirmCode = lazy(() => import('./pages/Setting/ConfirmCode').catch(urgeReload))
const NotificationSetting = lazy(() => import('./pages/Setting/NotificationSetting').catch(urgeReload))
const ClientApplicationSetting = lazy(() => import('./pages/Setting/ClientApplicationSetting').catch(urgeReload))
const DisconnectDevice = lazy(() => import('./pages/Setting/DisconnectDevice').catch(urgeReload))
const BirthDateSettingPage = lazy(() => import('./pages/Setting/BirthDateSettingPage').catch(urgeReload))
const PrivacyAndSafety = lazy(() => import('./pages/Setting/PrivacyAndSafety').catch(urgeReload))
const MutedUserList = lazy(() => import('./pages/Setting/MutedUserList').catch(urgeReload))
const Trash = lazy(() => import('./pages/Trash/Trash').catch(urgeReload))
const SearchedPhotos = lazy(() => import('./pages/Search/SearchedPhotos').catch(urgeReload))
const SearchedPhoto = lazy(() => import('./pages/Search/Photo').catch(urgeReload))
const HelpAndFeedback = lazy(() => import('./pages/HelpAndFeedback/HelpAndFeedback').catch(urgeReload))
const About = lazy(() => import('./pages/About/About').catch(urgeReload))
const FreuentlyAskedQuestions = lazy(() => import('./pages/About/FrequentlyAskedQuestions').catch(urgeReload))
const PrivacyPolicy = lazy(() => import('./pages/About/PrivacyPolicy').catch(urgeReload))
const ChildSafePolicy = lazy(() => import('./pages/About/ChildSafePolicy').catch(urgeReload))
const Tradelaw = lazy(() => import('./pages/About/Tradelaw').catch(urgeReload))
const Contact = lazy(() => import('./pages/About/Contact').catch(urgeReload))
const TagUserSelector = lazy(() => import('./pages/SelectTagUser/SelectTagUser').catch(urgeReload))
const Plan = lazy(() => import('./pages/Plan/Plan').catch(urgeReload))
const PlanManagement = lazy(() => import('./pages/Plan/PlanManagement').catch(urgeReload))
const PlanUpgradeResult = lazy(() => import('./pages/Plan/UpgradeResult').catch(urgeReload))
const Vket2025Summer = lazy(() => import('./pages/pr/vket2025summer/Vket2025Summer').catch(urgeReload))
const Honda2026 = lazy(() => import('./pages/pr/honda2026/Honda2026').catch(urgeReload))
const Honda2026TimelineViewer = lazy(() => import('./pages/pr/honda2026/Honda2026TimelineViewer').catch(urgeReload))
const Vket2025Winter = lazy(() => import('./pages/pr/vket2025winter/Vket2025Winter').catch(urgeReload))
const TimelineMainPhotoViewer = lazy(() => import('./pages/Timeline/MainTimelinePhotoViewer').catch(urgeReload))
const TimelineMePhotoViewer = lazy(() => import('./pages/Timeline/MeTimelinePhotoViewer').catch(urgeReload))
const StickerTutorial = lazy(() => import('./pages/About/StickerTutorial').catch(urgeReload))
const PCClientTutorial = lazy(() => import('./pages/About/PCClientTutorial').catch(urgeReload))
const DesktopCallback = lazy(() => import('./pages/Auth/DesktopCallback').catch(urgeReload))
const HistoryLayout = lazy(() => import('./pages/History/HistoryLayout').catch(urgeReload))
const WorldHistory = lazy(() => import('./pages/History/WorldHistory').catch(urgeReload))
const UserHistory = lazy(() => import('./pages/History/UserHistory').catch(urgeReload))
const UserHistoryPhoto = lazy(() => import('./pages/History/UserHistoryPhoto').catch(urgeReload))
const WorldHistoryPhoto = lazy(() => import('./pages/History/WorldHistoryPhoto').catch(urgeReload))
const HistoryErrorView = lazy(() => import('./pages/History/HistoryErrorView').catch(urgeReload))

// GlobalLayout内にSuspenceがあるので、GlobalLayoutより下位でSuspenceを使っていない

const router = createBrowserRouter([
  {
    Component () {
      usePageTracking() // useLocation()を使うのでこの位置
      useCapacitorBack()
      useDeeplinkListener()
      return <>
        <ScrollRestoration/>
        <AcceptRules>
          <Outlet/>
        </AcceptRules>
      </>
    },
    children: [
      {
        element: <AuthRedirect/>,
        children: [
          // FEATURE_DESKTOP_HANDOFF が false なら空配列（ページのチャンクも出力されない）
          ...desktopHandoffRoutes(<HeaderWithError/>),
          ...(is_history_enabled ? [{
            element: <HistoryLayout/>,
            errorElement: <HistoryErrorView/>,
            children: [{
              path: '/history/worlds',
              children: [
                {element: <GlobalLayout wide='full'/>, children: [{index: true, element: <WorldHistory/>}]},
                {path: ':joined_at/photo/:photo_id', element: <WorldHistoryPhoto/>},
              ],
            }, {
              path: '/history/users',
              children: [
                {element: <GlobalLayout wide='full'/>, children: [{index: true, element: <UserHistory/>}]},
                {path: ':user_id/encounters/:started_at/photo/:photo_id', element: <UserHistoryPhoto/>},
              ],
            }],
          }] : []),
          // Home は全幅スクロール（枠外でもスクロール）のため timeline バリアントの専用レイアウト
          ...(FEATURE_HOME ? [
            {
              element: <GlobalLayout wide='timeline' is_footer/>,
              children: [
                {
                  path: '/',
                  element: <Home/>,
                  errorElement: <HeaderWithError/>
                }
              ]
            }
          ] : []),
          {
            element: <GlobalLayout wide='middle' is_footer/>,
            children: [
              ...(!FEATURE_HOME ? [
                {
                  path: '/',
                  element: <Navigate to='/photo'/>,
                  errorElement: <HeaderWithError/>
                }
              ] : []),
              ...(FEATURE_DM ? [
                {
                  path: '/dm',
                  // アドレス帳ができるまでの仮。本来は <DMList/>（上の lazy import 参照）。
                  element: <DMListSearchFromAllUsers/>,
                  errorElement: <HeaderWithError/>
                }
              ] : []),
              is_dummy_api_mode ? {
                path: '/labo',
                element: <Labo/>,
                errorElement: <HeaderWithError/>
              } : {},
            ]
          },
          {
            element: <GlobalLayout wide='middle'/>,
            children: [
              ...FEATURE_SEARCH_PHOTO ? [
                {
                  path: '/search',
                  element: <Search/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              {
                path: '/notification',
                element: <Notification/>,
                errorElement: <HeaderWithError/>
              },
              ...FEATURE_FAVORITE ? [
                {
                  path: '/favorite/create',
                  element: <CreateFavorite/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/favorite/:favorite_id/edit',
                  element: <EditFavorite/>,
                  errorElement: <HeaderWithError/>
                },
              ] : [],
              ...FEATURE_ADDRESS_BOOK ? [
                {
                  path: '/address_book',
                  element: <AddressBook/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/book_user',
                  element: <BookUser/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/group/:group_id',
                  element: <Group/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/group/:group_id/edit',
                  element: <GroupEditor/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/group/create',
                  element: <GroupEditor/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/group_user_selector',
                  element: <GroupUserSelector/>,
                  errorElement: <HeaderWithError/>
                },
              ] : [],
              ...FEATURE_SHARE ? [
                {
                  path: '/share/:share_id/select_owner',
                  element: <SelectOwner/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/share/:share_id/add_users',
                  element: <AddUsers/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/share/:share_id/approval_awaiting',
                  element: <ApprovalAwaitingList/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/share/:share_id/invitation',
                  element: <InvitationList/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/share/:share_id/member',
                  element: <ShareAlbumMember/>,
                  errorElement: <HeaderWithError/>
                },
              ] : [],
              ...FEATURE_ALBUM ? [
                {
                  path: '/album/create',
                  element: <CreateAlbum/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              ...FEATURE_SHARE ? [
                {
                  path: '/share/create',
                  element: <CreateShareAlbum/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              {
                path: '/user/:user_id',
                element: <UserRouter/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/edit_profile',
                element: <EditProfile/>,
                errorElement: <HeaderWithError/>
              },
              ...FEATURE_SELECT_TAG_USER ? [
                {
                  path: '/select_tag_user',
                  element: <TagUserSelector/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              ...(FEATURE_DM ? [
                {
                  path: '/dm/create',
                  element: <CreateDM/>,
                  errorElement: <HeaderWithError/>
                }
              ] : []),
              ...FEATURE_SHARE ? [
                {
                  path: '/share/:share_id/edit',
                  element: <EditShareAlbum/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              ...FEATURE_ALBUM ? [
                {
                  path: '/album/:album_id/edit',
                  element: <EditAlbum/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/album/:album_id/change_share',
                  element: <ChangeShare/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              {
                path: '/setting',
                element: <Setting/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/display_and_accessibility',
                element: <DisplayAndAccessibilitySetting/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/account',
                element: <AccountSetting/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/change_password',
                element: <ChangeAccountPassword/>,
                errorElement: <HeaderWithError/>
              },
              ...FEATURE_PASSKEY ? [
                {
                  path: '/setting/passkeys',
                  element: <PasskeySetting/>,
                  errorElement: <HeaderWithError/>
                },
              ] : [],
              {
                path: '/setting/change_email',
                loader: emailLoader,
                element: <ChangeAccountEmail/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/confirm_code',
                element: <ConfirmCode/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/notification',
                element: <NotificationSetting/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/client_application',
                element: <ClientApplicationSetting/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/disconnect_device',
                //loader: clientApplicationLoader,
                element: <DisconnectDevice/>,
                errorElement: <HeaderWithError/>
              },
              {
                path: '/setting/birthdate',
                element: <BirthDateSettingPage/>,
                errorElement: <HeaderWithError/>
              },
              ...FEATURE_MUTE_USER ? [
                {
                  path: '/setting/privacy_and_safety',
                  element: <PrivacyAndSafety/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/setting/privacy_and_safety/muted_users',
                  element: <MutedUserList/>,
                  errorElement: <HeaderWithError/>
                },
              ] : [],
              ...FEATURE_TRASH ? [
                {
                  path: '/trash',
                  element: <Trash/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              ...FEATURE_PLAN ? [
                {
                  path: '/plan',
                  element: <Plan/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/plan/management',
                  element: <PlanManagement/>,
                  errorElement: <HeaderWithError/>
                },
                {
                  path: '/plan/upgrade_result',
                  element: <PlanUpgradeResult/>,
                  errorElement: <HeaderWithError/>
                }
              ] : []
            ]
          },
          {
            element: <GlobalLayout wide='full' is_footer/>,
            children: [
              ...FEATURE_ALBUM ? [
                {
                  path: '/album',
                  element: <MyAlbum/>,
                  errorElement: <HeaderWithError/>
                }
              ] : [],
              ...FEATURE_SHARE ? [
                {
                  path: '/share',
                  element: <ShareAlbumList/>,
                  errorElement: <HeaderWithError/>
                }
              ] : []
            ]
          },
          ...FEATURE_FAVORITE ? [{
            element: <GlobalLayout wide='full'/>,
            children: [
              {
                path: '/favorite',
                element: <FavoriteList/>,
                errorElement: <HeaderWithError/>
              },
            ]
          }] : [],
          {
            path: '/photo',
            Component () {
              const queryClient = useQueryClient()
              return <PhotosCacheProvider
                getPhoto={PhotoAPI.getPhoto.bind(PhotoAPI)}
                tagsToPhoto={PhotoAPI.tagsToMyPhoto.bind(PhotoAPI)}
                reactionToPhoto={PhotoAPI.reactionToMyPhoto.bind(PhotoAPI)}
                getDirectory={()=> Promise.resolve({data: undefined, error: undefined, response: new Response()})}
                listPhotos={PhotoAPI.listPhotos.bind(PhotoAPI)}
                listAllPhotosDate={PhotoAPI.listAllPhotosDate.bind(PhotoAPI)}
                // マイフォト一覧の選択削除（MyPhoto.tsx の photo_cache.removePhotos）がここを通る
                removePhotos={photo_ids => invalidateTrashOnSuccess(
                  queryClient,
                  () => PhotoAPI.movePhotosToTrash(photo_ids),
                )}
              />
            },
            children: [
              {
                element: <GlobalLayout wide='full' is_footer/>,
                children: [
                  {
                    path: '',
                    element: <MyPhoto/>,
                    errorElement: <HeaderWithError/>
                  }
                ]
              },
              {
                path: ':photo_id',
                element: <MyPhotoPhoto/>,
                errorElement: <HeaderWithError/>
              },
            ]
          },
          ...FEATURE_FAVORITE ? [{
            path: '/favorite/:favorite_id',
            Component () {
              const id = useParams().favorite_id || ''
              const queryClient = useQueryClient()
              return <PhotosCacheProvider key={id}
                getDirectory={FavoriteAPI.getFavorite.bind(FavoriteAPI, id)}
                listAllPhotosDate={FavoriteAPI.listAllPhotosDateFromFavorite.bind(FavoriteAPI, id)}
                getPhoto={FavoriteAPI.getPhotoFromFavorite.bind(FavoriteAPI, id)}
                tagsToPhoto={FavoriteAPI.tagsToFavoritePhoto.bind(FavoriteAPI, id)}
                reactionToPhoto={FavoriteAPI.reactionToFavoritePhoto.bind(FavoriteAPI, id)}
                listPhotos={FavoriteAPI.listPhotosFromFavorite.bind(FavoriteAPI, id)}
                removePhotos={photo_ids => invalidateFavoriteListOnSuccess(
                  queryClient,
                  () => FavoriteAPI.removePhotosFromAllFavorites(photo_ids),
                )}
              />
            },
            children: [
              {
                element: <GlobalLayout wide='full'/>,
                children: [
                  {
                    path: '',
                    element: <Favorite/>,
                    errorElement: <HeaderWithError/>
                  }
                ]
              },
              {
                path: 'photo/:photo_id',
                element: <FavoritePhoto/>,
                errorElement: <HeaderWithError/>
              },
            ]
          }] : [],
          ...(FEATURE_DM ? [
            {
              path: '/dm/:user_id',
              Component () {
                const id = useParams().user_id || ''
                return <PhotosCacheProvider key={id}
                  getDirectory={DMAPI.getDM.bind(DMAPI, id)}
                  listAllPhotosDate={DMAPI.listAllPhotosDateFromDM.bind(DMAPI, id)}
                  getPhoto={DMAPI.getPhotoFromDM.bind(DMAPI, id)}
                  tagsToPhoto={DMAPI.tagsToDMPhoto.bind(DMAPI, id)}
                  reactionToPhoto={DMAPI.reactionToDMPhoto.bind(DMAPI, id)}
                  listPhotos={DMAPI.listPhotosFromDM.bind(DMAPI, id)}
                  removePhotos={DMAPI.removePhotosFromDM.bind(DMAPI, id)}
                />
              },
              children: [
                {
                  element: <GlobalLayout wide='full'/>,
                  children: [
                    {
                      path: '',
                      element: <DM/>,
                      errorElement: <HeaderWithError/>
                    }
                  ]
                },
                {
                  path: 'photo/:photo_id',
                  element: <DMPhoto/>,
                  errorElement: <HeaderWithError/>
                },
              ]
            }
          ] : []),
          ...FEATURE_ALBUM ? [
            {
              path: '/album/:album_id',
              Component () {
                const id = useParams().album_id || ''
                return <AlbumCacheProvider
                  key={id}
                  getAlbum={AlbumAPI.getAlbum.bind(AlbumAPI, id)}
                  getPhoto={AlbumAPI.getPhotoFromAlbum.bind(AlbumAPI, id)}
                  tagsToPhoto={AlbumAPI.tagsToAlbumPhoto.bind(AlbumAPI, id)}
                  reactionToPhoto={AlbumAPI.reactionToAlbumPhoto.bind(AlbumAPI, id)}
                  removePhotos={AlbumAPI.removePhotosFromAlbum.bind(AlbumAPI, id)}
                />
              },
              children: [
                {
                  element: <GlobalLayout wide='full'/>,
                  children: [
                    {
                      path: '',
                      element: <Album/>,
                      errorElement: <HeaderWithError/>
                    },
                  ]
                },
                {
                  path: 'photo/:photo_id',
                  element: <AlbumPhoto/>,
                  errorElement: <HeaderWithError/>
                },
              ]
            }
          ] : [],
          ...FEATURE_SEARCH_PHOTO ? [
            {
              Component () {
                const [search_params] = useSearchParams()
                const owner_id = search_params.get('owner') || undefined
                const in_user_id = search_params.get('in_user') || undefined
                const in_vrchat_name = search_params.get('in_vrchat') || undefined
                const queryClient = useQueryClient()
                return <SearchPhotosCacheProvider
                  key={JSON.stringify([owner_id, in_user_id, in_vrchat_name])}
                  getUser={UserAPI.getUser.bind(UserAPI, owner_id || in_user_id || '')}
                  searchPhotos={SearchAPI.searchPhotos.bind(SearchAPI, {owner_id, in_user_id, in_vrchat_name})}
                  getPhoto={SearchAPI.getPhotoFromSearch.bind(SearchAPI, {owner_id, in_user_id, in_vrchat_name})}
                  tagsToPhoto={SearchAPI.tagsToSearchPhoto.bind(SearchAPI, {owner_id, in_user_id, in_vrchat_name})}
                  reactionToPhoto={SearchAPI.reactionToSearchPhoto.bind(SearchAPI, {owner_id, in_user_id, in_vrchat_name})}
                  searchAllPhotosDateWithId={SearchAPI.searchAllPhotosDateWithId.bind(SearchAPI, {owner_id, in_user_id, in_vrchat_name})}
                  removePhotos={photo_ids => invalidateTrashOnSuccess(
                    queryClient,
                    () => PhotoAPI.movePhotosToTrash(photo_ids),
                  )} // 使わないけどいちおう
                />
              },
              children: [
                {
                  element: <GlobalLayout wide='full'/>,
                  children: [
                    {
                      path: '/search/photos',
                      element: <SearchedPhotos/>,
                      errorElement: <HeaderWithError/>
                    }
                  ]
                },
                {
                  path: '/search/photo/:photo_id',
                  element: <SearchedPhoto/>,
                  errorElement: <HeaderWithError/>
                },
              ]
            }
          ] : []
        ]
      },
    ]
  },
  {
    Component () {
      usePageTracking()
      useCapacitorBack()
      useDeeplinkListener()
      return <>
        <ScrollRestoration/>
        <Suspense>
          <Outlet/>
        </Suspense>
      </>
    },
    children: [
      {
        path: '/login',
        element: <Login/>,
        errorElement: <HeaderWithError/>
      },
      {
        element: <GlobalLayout wide='middle'/>,
        children: [
          {
            path: '/help',
            element: <HelpAndFeedback/>,
            errorElement: <HeaderWithError/>
          }
        ]
      },
      {
        path: '/about',
        element: <About/>,
        loader: aboutLoader,
        errorElement: <HeaderWithError/>
      },
      {
        element: <AboutLocalLayout/>,
        children: [
          {
            path: '/about/help',
            element: <FreuentlyAskedQuestions/>,
            loader: faqLoader,
            errorElement: <HeaderWithError/>
          },
          {
            path: '/about/privacy_policy',
            element: <PrivacyPolicy/>,
            loader: privacypolicyLoader,
            errorElement: <HeaderWithError/>
          },
          {
            path: '/about/child_safety_policy',
            element: <ChildSafePolicy/>,
            loader: childsafetyLoader,
            errorElement: <HeaderWithError/>
          },
          {
            path: '/about/terms',
            element: <Tradelaw/>,
            loader: tradelawLoader,
            errorElement: <HeaderWithError/>
          },
          {
            path: '/about/contact',
            element: <Contact/>,
            loader: contactLoader,
            errorElement: <HeaderWithError/>
          },
        ],
      },
      ...(FEATURE_STICKER ? [{
        path: '/about/sticker_tutorial',
        element: <StickerTutorial/>,
        errorElement: <HeaderWithError/>
      }] : []),
      {
        path: '/about/client_tutorial',
        element: <PCClientTutorial/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/auth/desktop-callback',
        element: <DesktopCallback/>,
        errorElement: <HeaderWithError/>
      },
      ...FEATURE_SHARE ? [
        {
          path: '/share/:share_id',
          Component () {
            type Share = OpenAPIResponseData<ReturnType<typeof ShareAPI.getShare>>
            const share_id = useParams().share_id || ''
            const getPassword = useGetCacheSharePassword(share_id)
            return <AcceptRules>
              <AlbumCacheProvider<Share>
                key={share_id}
                getAlbum={s=> ShareAPI.getShare(share_id, getPassword(), s)}
                getPhoto={id=> ShareAPI.getPhotoFromShare(share_id, id, getPassword())}
                tagsToPhoto={ShareAPI.tagsToSharePhoto.bind(ShareAPI, share_id)}
                reactionToPhoto={ShareAPI.reactionToSharePhoto.bind(ShareAPI, share_id)}
                removePhotos={ShareAPI.removePhotosFromShare.bind(ShareAPI, share_id)}
              />
            </AcceptRules>
          },
          children: [
            {
              element: <GlobalLayout wide='full'/>,
              children: [
                {
                  path: '',
                  element: <ShareAlbum/>,
                  errorElement: <HeaderWithError/>
                },
              ]
            },
            {
              path: 'photo/:photo_id',
              element: <SharePhoto/>,
              errorElement: <HeaderWithError/>
            },
          ]
        }
      ] : [],
      {
        path: '/pr/vket2025s',
        element: <Vket2025Summer/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/pr/vket2025summer',
        element: <Vket2025Summer/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/pr/vket2025winter',
        element: <Vket2025Winter/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/pr/honda_motanion_2026',
        element: <Honda2026/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/pr/honda_motanion_2026/photo/:timestamp',
        element: <Honda2026TimelineViewer/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/pr/vket2025winter/grand_master',
        element: <Vket2025Winter/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/pr/vket2025winter/grand_master',
        element: <Vket2025Winter/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/timeline/main/photo/:timestamp',
        element: <TimelineMainPhotoViewer/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '/timeline/me/photo/:timestamp',
        element: <TimelineMePhotoViewer/>,
        errorElement: <HeaderWithError/>
      },
      {
        path: '*',
        element: <HeaderWith404/>,
      },
    ]
  }
], {
  basename
})

const Root: React.FC = ()=> <RouterProvider router={router}/>

export default Root
